import { ChalkEmployeeGuard } from "@/components/admin/ChalkEmployeeGuard";
import { DynamicCommandPaletteContainer } from "@/components/command-palette/DynamicCommandPalette";
import { ChalkLayoutErrorComponent } from "@/components/layout/ErrorBoundary";
import { LayoutV2 } from "@/components/layout/LayoutV2";
import { LayoutWarningBanners } from "@/components/layout/LayoutWarningBanners";
import { EnvironmentNotFoundError } from "@/components/networking/EnvironmentNotFoundError";
import { SuspendedEnvironmentWarning } from "@/components/networking/SuspendedEnvironmentWarning";
import {
  GOOGLE_ANALYTICS_SITE_VERIFICATION_ID,
  GOOGLE_SEARCH_CONSOLE_SITE_VERIFICATION_ID,
} from "@/utils/analytics/google-site-verification";
import { classNames, cx } from "@/utils/classnames";
import { useRumIdentity } from "@/utils/datadog-init";
import {
  canonicalUrlIsProduction,
  useCurrentCanonicalUrl,
} from "@/utils/hooks/url/use-current-url";
import {
  useAmplitudeBaseIdentify,
  useAmplitudePageview,
} from "@/utils/hooks/use-amplitude-pageview";
import { useAutoLogout } from "@/utils/hooks/use-auto-logout";
import { useAutoSessionRefresh } from "@/utils/hooks/use-auto-session-refresh";
import { useEnvironmentTitle } from "@/utils/hooks/use-env-title";
import { useFaviconPath } from "@/utils/hooks/use-favicon-path";
import { MDXProvider } from "@mdx-js/react";
import { ErrorBoundary } from "next/dist/client/components/error-boundary";
import Head from "next/head";
import React, { PropsWithChildren, ReactNode, useMemo } from "react";
import { AmplitudeEvent, AmplitudeScript } from "utils/amplitude";
import { NavBar, SimpleNavBar } from "../navigation/NavBar";
import { Girdle, NarrowGirdle } from "./Girdle";

export interface LayoutProps extends PropsWithChildren {
  allowNarrowWidth?: boolean;
  /**
   * Element rendered underneath the top rail. E.g. "branch" label or "deployment" label on deployment and branch deployment pages, respectively.
   */
  absoluteLabel?: ReactNode;
  containerClassName?: string;
  fullWidthLayout?: boolean;
  loggedIn?: boolean;
  /**
   * Extra classes for the main scroll/content container. Use sparingly for pages
   * with canvas-like layouts that need to opt out of default document padding.
   */
  mainContentClassName?: string;
  /**
   * Extra classes for the content wrapper inside the main scroll container. This is useful when a
   * page needs its own internal layout to fill the post-banner content pane height.
   */
  mainContentWrapperClassName?: string;
  /**
   * Let the browser document own vertical scrolling instead of the layout's inner
   * content pane. This is for full-page fixtures whose scrollbar should sit at
   * the viewport edge.
   */
  useDocumentScroll?: boolean;
  /**
   * Most document-like pages need bottom breathing room. Full-height canvas/table
   * pages own their viewport height and should opt out to avoid dead scroll space.
   */
  mainContentHasBottomPadding?: boolean;
  /**
   * Left-side navbar. Examples:
   * - project navbar (environment-scoped pages). Most common. E.g. /projects/<p>/environments/<e>/*
   * - team navbar (team-scoped pages). Only used on a few pages. E.g. /projects
   * - deployment navbar (deployment-scoped pages). Pages scoped to a particular deployment. E.g. /projects/<p>/environments/<e>/deployments/<d>/*
   * - branch-deployment navbar (branch deployment-scoped pages). Pages scoped to a particular branch deployment. E.g. /projects/<p>/environments/<e>/branches/<b>/deployments/<d>/*
   */
  navBar?: ReactNode;
  /** Hide the left-side navigation while preserving the V2 top rail and content shell. */
  hideSideNav?: boolean;
  /** Visually defocus and disable the V2 top rail while page content owns interaction. */
  defocusTopRail?: boolean;
  noNav?: boolean;
  /**
   * Second layer left-side navbar, e.g. for settings panes.
   */
  secondaryNavbar?: ReactNode;
  title?: string;
  /**
   * Element above main content, e.g. offline query details page progress bar.
   */
  topAccessory?: ReactNode;
}

export const LAYOUT_TOP_PADDING_CLASSNAME = "pt-[26px]";
export const LAYOUT_X_PADDING_CLASSNAME = "px-10";
export const LAYOUT_LEFT_PADDING_CLASSNAME = "pl-10";
export const LAYOUT_RIGHT_PADDING_CLASSNAME = "pr-10";

export const Layout: React.FC<LayoutProps> = ({ children, ...props }) => {
  return <LayoutV2 {...props}>{children}</LayoutV2>;
};

export const LayoutV1: React.FC<LayoutProps> = (props) => {
  const {
    loggedIn,
    navBar,
    hideSideNav,
    fullWidthLayout,
    secondaryNavbar,
    children,
    title = "",
    noNav,
    containerClassName,
    mainContentHasBottomPadding = true,
    absoluteLabel,
    topAccessory,
  } = props;
  useAmplitudePageview(AmplitudeEvent.VIEW_PAGE);
  useAmplitudeBaseIdentify();
  useAutoLogout();
  useAutoSessionRefresh();
  useRumIdentity();

  const environmentTitle = useEnvironmentTitle(title);

  const faviconPath = useFaviconPath();

  return (
    <>
      {loggedIn && <DynamicCommandPaletteContainer />}
      <div
        className="design-system-typography min-h-screen flex flex-col bg-white"
        style={{ minWidth: "960px" }}
      >
        <Head>
          <title>{environmentTitle}</title>
          <meta charSet="utf-8" />
          <meta name="viewport" content="initial-scale=1.0, width=device-width" />
          <link rel="shortcut icon" href={faviconPath} />
          <AmplitudeScript />
        </Head>
        {!noNav && (
          <header className="bg-white sticky top-0 z-10">
            <NavBar loggedIn={loggedIn} absoluteLabel={absoluteLabel} />
          </header>
        )}

        <div className={classNames("flex flex-row flex-grow", containerClassName)}>
          {navBar && !hideSideNav && <div className="bg-white">{navBar}</div>}
          {secondaryNavbar && <div className="bg-white">{secondaryNavbar}</div>}
          <div
            className={cx("flex-1", mainContentHasBottomPadding && "pb-16")}
            style={{ minWidth: "0px" }}
          >
            {topAccessory}
            <ChalkEmployeeGuard>
              <LayoutWarningBanners loggedIn={loggedIn} />
            </ChalkEmployeeGuard>
            {loggedIn && <EnvironmentNotFoundError />}
            {loggedIn && <SuspendedEnvironmentWarning />}
            <div
              className={cx(
                !fullWidthLayout && LAYOUT_TOP_PADDING_CLASSNAME,
                !fullWidthLayout && LAYOUT_X_PADDING_CLASSNAME,
              )}
            >
              <Girdle>
                <ErrorBoundary errorComponent={ChalkLayoutErrorComponent}>{children}</ErrorBoundary>
              </Girdle>
            </div>
          </div>
        </div>
      </div>
      <footer className="design-system-typography bg-white p-4 text-cds-text-secondary text-cds-sm text-center">
        © Chalk AI, Inc {new Date().getFullYear()}
      </footer>
    </>
  );
};

export default Layout;

export interface SimpleLayoutProps extends PropsWithChildren {
  absoluteLabel?: ReactNode;
  containerClassName?: string;
  fullWidthLayout?: boolean;
  navBar?: ReactNode;
  noNav?: boolean;
  secondaryNavbar?: ReactNode;
  title?: string;
  topAccessory?: ReactNode;
}

export const SimpleLayout: React.FC<SimpleLayoutProps> = ({
  navBar,
  fullWidthLayout,
  secondaryNavbar,
  children,
  title = "",
  noNav,
  containerClassName,
  absoluteLabel,
  topAccessory,
}) => {
  useAmplitudePageview(AmplitudeEvent.VIEW_PAGE);

  const environmentTitle = useEnvironmentTitle(title);

  const faviconPath = useFaviconPath();

  return (
    <>
      <div
        className="design-system-typography min-h-screen flex flex-col bg-white"
        style={{ minWidth: "960px" }}
      >
        <Head>
          <title>{environmentTitle}</title>
          <meta charSet="utf-8" />
          <meta name="viewport" content="initial-scale=1.0, width=device-width" />
          <link rel="shortcut icon" href={faviconPath} />
          <AmplitudeScript />
        </Head>
        {!noNav && (
          <header className="bg-white sticky top-0 z-10">
            <NavBar loggedIn={false} absoluteLabel={absoluteLabel} />
          </header>
        )}

        <div className={classNames("flex flex-row flex-grow", containerClassName)}>
          {navBar && <div className="bg-white">{navBar}</div>}
          {secondaryNavbar && <div className="bg-white">{secondaryNavbar}</div>}
          <div className="flex-1 pb-16" style={{ minWidth: "0px" }}>
            {topAccessory}
            <div
              className={cx(
                !fullWidthLayout && "pt-[26px]",
                !fullWidthLayout && LAYOUT_X_PADDING_CLASSNAME,
              )}
            >
              <Girdle>{children}</Girdle>
            </div>
          </div>
        </div>
      </div>
      <footer className="design-system-typography bg-white p-4 text-cds-text-secondary text-cds-sm text-center">
        © Chalk AI, Inc {new Date().getFullYear()}
      </footer>
    </>
  );
};

export interface ExtremelySimpleLayoutProps extends PropsWithChildren {}

export const ExtremelySimpleLayout: React.FC<ExtremelySimpleLayoutProps> = ({ children }) => {
  const environmentTitle = useEnvironmentTitle("Chalk AI");
  const faviconPath = useFaviconPath();
  return (
    <>
      <div
        className="design-system-typography min-h-screen flex flex-col bg-white"
        style={{ minWidth: "960px" }}
      >
        <Head>
          <title>{environmentTitle}</title>
          <meta charSet="utf-8" />
          <meta name="viewport" content="initial-scale=1.0, width=device-width" />
          <link rel="shortcut icon" href={faviconPath} />
          <AmplitudeScript />
        </Head>
        <header className="bg-white sticky top-0 z-10">
          <SimpleNavBar />
        </header>

        <div className={classNames("flex flex-row flex-grow")}>
          <div className="flex-1 pb-16" style={{ minWidth: "0px" }}>
            <div className={cx("pt-[26px]", LAYOUT_X_PADDING_CLASSNAME)}>
              <Girdle>{children}</Girdle>
            </div>
          </div>
        </div>
      </div>
      <footer className="design-system-typography bg-white p-4 text-cds-text-secondary text-cds-sm text-center">
        © Chalk AI, Inc {new Date().getFullYear()}
      </footer>
    </>
  );
};

export const LegalDocLayout: React.FC<LayoutProps> = ({
  loggedIn,
  navBar,
  children,
  title = "",
  noNav,
}) => {
  const currentUrl = useCurrentCanonicalUrl();
  const urlIsProduction = useMemo(() => canonicalUrlIsProduction(currentUrl), [currentUrl]);
  const faviconPath = useFaviconPath();
  return (
    <>
      <div className="min-h-screen flex flex-col bg-white font-everett">
        <Head>
          <title>{`${title} | Chalk`}</title>
          <meta charSet="utf-8" />
          <meta name="viewport" content="initial-scale=1.0, width=device-width" />
          <link rel="shortcut icon" href={faviconPath} />
          <link rel="canonical" href={currentUrl} />
          <meta name="google-site-verification" content={GOOGLE_ANALYTICS_SITE_VERIFICATION_ID} />
          <meta
            name="google-site-verification"
            content={GOOGLE_SEARCH_CONSOLE_SITE_VERIFICATION_ID}
          />
          {urlIsProduction ? (
            <meta name={"robots"} content={"index,follow"} />
          ) : (
            <meta name={"robots"} content={"noindex,nofollow"} />
          )}
          <AmplitudeScript />
        </Head>
        {!noNav && (
          <header className="bg-white shadow">
            <NavBar loggedIn={loggedIn} />
            {navBar}
          </header>
        )}

        <div className="flex-1 px-4 sm:px-6 md:px-8 lg:px-10 pt-4 pb-16">
          <NarrowGirdle>{children}</NarrowGirdle>
        </div>
      </div>
      <footer className="bg-white p-4 text-cds-text-secondary text-cds-sm text-center">
        © Chalk AI, Inc {new Date().getFullYear()}
      </footer>
    </>
  );
};

export interface LegalLayoutMeta {
  title?: string;
  description?: string;
  keywords?: string;
}

export interface LegalDocLayoutMDXProps extends Omit<LayoutProps, "title"> {
  meta?: LegalLayoutMeta;
}

export const LegalDocLayoutMDX: React.FC<LegalDocLayoutMDXProps> = ({
  loggedIn,
  navBar,
  children,
  meta,
  noNav,
}) => {
  const currentUrl = useCurrentCanonicalUrl();
  const faviconPath = useFaviconPath();
  const renderedTitle = meta?.title ? `${meta.title} | Chalk` : "Chalk";
  return (
    <>
      <div className="min-h-screen flex flex-col bg-white font-everett">
        <Head>
          <title>{`${renderedTitle}`}</title>
          <meta charSet="utf-8" />
          <meta name="viewport" content="initial-scale=1.0, width=device-width" />
          <link rel="shortcut icon" href={faviconPath} />
          <link rel="canonical" href={currentUrl} />
          <meta name="google-site-verification" content={GOOGLE_ANALYTICS_SITE_VERIFICATION_ID} />
          <meta
            name="google-site-verification"
            content={GOOGLE_SEARCH_CONSOLE_SITE_VERIFICATION_ID}
          />
          {meta?.description != null && <meta name="description" content={meta.description} />}
          {meta?.keywords != null && <meta name="keywords" content={meta.keywords} />}
          <AmplitudeScript />
        </Head>
        {!noNav && (
          <header className="bg-white shadow">
            <NavBar loggedIn={loggedIn} />
            {navBar}
          </header>
        )}

        <div className="flex-1 px-4 sm:px-6 md:px-8 lg:px-10 pt-4 pb-16 legal-mdx-layout">
          <NarrowGirdle>
            <MDXProvider>{children}</MDXProvider>
          </NarrowGirdle>
        </div>
      </div>
      <footer className="bg-white p-4 text-cds-text-secondary text-cds-sm text-center">
        © Chalk AI, Inc {new Date().getFullYear()}
      </footer>
    </>
  );
};
